/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type {
  Draft,
  PayloadAction,
  Reducer,
  UnknownAction,
} from '@reduxjs/toolkit';
import { createAction, createSlice } from '@reduxjs/toolkit';
import type { SoundingsCollection } from '@opengeoweb/shared';
import type { SetTimeSyncActionPayload } from '@opengeoweb/store';
import type {
  SoundingsStore,
  RegisterSoundingsPresetPayload,
  UpdateLocationPayload,
  UpdateSourcePayload,
  UpdateTimePayload,
  UpdateDiagramTypePayload,
  ToggleLocationMarkersPayload,
  AddSoundingPanelsPayload,
  ChangeSoundingPanelOrderPayload,
  SoundingsState,
} from './types';
import { getPanelIdFromAction, isRegisteredPanelId } from './utils';

export const initialSoundingsState: SoundingsStore = {
  selectedSources: {},
  selectedLocations: {},
  selectedTime: new Date().toISOString(),
  diagramType: 'SKEWT',
  soundingPanels: {
    order: ['soundings', 'windbarbs', 'hodograph'],
    entities: {
      soundings: {
        id: 'soundings',
        name: 'Soundings',
        isDisplayed: true,
      },
      windbarbs: {
        id: 'windbarbs',
        name: 'Wind Barbs',
        isDisplayed: true,
      },
      hodograph: {
        id: 'hodograph',
        name: 'Hodograph',
        isDisplayed: true,
      },
    },
  },
  shouldHideLocationMarkers: false,
};

const initialState: SoundingsState = {
  byId: {},
  registeredPanelIds: [],
  soundingsCollections: [],
};

const slice = createSlice({
  initialState: initialSoundingsState,
  name: 'soundings',
  reducers: {
    registerSoundingsPanel: (
      draft: Draft<SoundingsStore>,
      _payloadAction: PayloadAction<{ panelId: string }>,
    ) => {
      void draft;
    },
    registerSoundingsPreset: (
      draft: Draft<SoundingsStore>,
      payloadAction: PayloadAction<RegisterSoundingsPresetPayload>,
    ) => {
      const { preset } = payloadAction.payload;
      if (preset) {
        draft.selectedSources = preset.selectedSources ?? {};
        draft.selectedLocations = preset.selectedLocations ?? {};
        draft.selectedTime = preset.selectedTime ?? new Date().toISOString();
        draft.diagramType = preset.diagramType ?? 'SKEWT';
        // Ensure we show all available panels, even if not stored in preset
        if (preset.soundingPanels) {
          draft.soundingPanels = { ...preset.soundingPanels };
          initialSoundingsState.soundingPanels.order.forEach((panel) => {
            if (
              !preset.soundingPanels.order.find(
                (presetPanel) => panel === presetPanel,
              )
            ) {
              draft.soundingPanels.order = [
                ...draft.soundingPanels.order,
                panel,
              ];
              draft.soundingPanels.entities = {
                ...draft.soundingPanels.entities,
                [panel]: initialSoundingsState.soundingPanels.entities[panel],
              };
            }
          });
        } else {
          draft.soundingPanels = initialSoundingsState.soundingPanels;
        }

        draft.shouldHideLocationMarkers =
          preset.shouldHideLocationMarkers ?? false;
      }
    },
    updateSelectedSource: (
      draft: Draft<SoundingsStore>,
      payloadAction: PayloadAction<UpdateSourcePayload>,
    ) => {
      const { sourceId, value } = payloadAction.payload;
      draft.selectedSources[sourceId] = {
        ...draft.selectedSources[sourceId],
        ...value,
      };
    },
    updateSelectedLocation: (
      draft: Draft<SoundingsStore>,
      payloadAction: PayloadAction<UpdateLocationPayload>,
    ) => {
      const { sourceId, location } = payloadAction.payload;
      draft.selectedLocations[sourceId] = location;
    },
    updateSelectedTime: (
      draft: Draft<SoundingsStore>,
      payloadAction: PayloadAction<UpdateTimePayload>,
    ) => {
      const { selectedTime } = payloadAction.payload;
      draft.selectedTime = selectedTime;
    },
    updateDiagramType: (
      draft: Draft<SoundingsStore>,
      payloadAction: PayloadAction<UpdateDiagramTypePayload>,
    ) => {
      draft.diagramType = payloadAction.payload.diagramType;
    },
    toggleLocationMarkers: (
      draft: Draft<SoundingsStore>,
      payloadAction: PayloadAction<ToggleLocationMarkersPayload>,
    ) => {
      const { shouldHideLocationMarkers } = payloadAction.payload;
      draft.shouldHideLocationMarkers = shouldHideLocationMarkers;
    },
    addSoundingPanelsFromPreset: (
      draft: Draft<SoundingsStore>,
      payloadAction: PayloadAction<AddSoundingPanelsPayload>,
    ) => {
      const { order, entities } = payloadAction.payload.panels;
      draft.soundingPanels.order = order;
      draft.soundingPanels.entities = entities;
    },
    changeSoundingPanelOrder: (
      draft: Draft<SoundingsStore>,
      payloadAction: PayloadAction<ChangeSoundingPanelOrderPayload>,
    ) => {
      const { newOrderOfIds } = payloadAction.payload;
      draft.soundingPanels.order = newOrderOfIds;
    },
    toggleSoundingPanelVisibility: (
      draft: Draft<SoundingsStore>,
      payloadAction: PayloadAction<{
        id: string;
        panelId?: string;
        origin?: string;
      }>,
    ) => {
      const { id } = payloadAction.payload;
      const panel = draft.soundingPanels.entities[id];
      if (panel) {
        panel.isDisplayed = !panel.isDisplayed;
      }
    },
  },
});

const setSoundingsCollections = createAction<SoundingsCollection[]>(
  'soundings/setCollections',
);

export const soundingsActions = {
  ...slice.actions,
  setSoundingsCollections,
};

const shouldHandlePanelAction = (
  state: SoundingsState,
  action: { payload?: { panelId?: string } },
): boolean => {
  return isRegisteredPanelId(state, getPanelIdFromAction(action));
};

const normalizeSoundingsState = (
  state: SoundingsState | SoundingsStore,
): SoundingsState => {
  if ('byId' in state) {
    return state;
  }
  return {
    ...initialState,
    byId: {
      ...initialState.byId,
      default: state,
    },
    registeredPanelIds: ['default'],
  };
};

export const soundingsReducer: Reducer<SoundingsState> = (
  state: SoundingsState | undefined,
  action: UnknownAction,
): SoundingsState => {
  const normalizedState = normalizeSoundingsState(state ?? initialState);
  if (action.type === 'GENERIC_SYNC_SETTIME') {
    const { source, targets: targetsFromAction } = (
      action as PayloadAction<SetTimeSyncActionPayload>
    ).payload;
    const targets = [...targetsFromAction];

    if (source?.payload) {
      targets.unshift({
        targetId: source.payload.sourceId,
        value: source.payload.value,
      });
    }

    let hasChanges = false;
    const nextById = { ...normalizedState.byId };

    targets.forEach(({ targetId, value }) => {
      if (!targetId || !value?.currentTime || !nextById[targetId]) {
        return;
      }
      if (nextById[targetId].selectedTime === value.currentTime) {
        return;
      }
      hasChanges = true;
      nextById[targetId] = {
        ...nextById[targetId],
        selectedTime: value.currentTime,
      };
    });

    if (hasChanges) {
      return {
        ...normalizedState,
        byId: nextById,
      };
    }
    return normalizedState;
  }
  if (action.type === setSoundingsCollections.type) {
    const collections = (action as PayloadAction<SoundingsCollection[]>)
      .payload;
    if (collections === normalizedState.soundingsCollections) {
      return normalizedState;
    }
    return {
      ...normalizedState,
      soundingsCollections: collections,
    };
  }
  if (action.type === slice.actions.registerSoundingsPanel.type) {
    const { panelId } = (action as PayloadAction<{ panelId: string }>).payload;
    if (!panelId) {
      return normalizedState;
    }
    if (normalizedState.registeredPanelIds?.includes(panelId)) {
      return normalizedState;
    }
    return {
      ...normalizedState,
      registeredPanelIds: [
        ...(normalizedState.registeredPanelIds ?? []),
        panelId,
      ],
    };
  }
  if (
    !shouldHandlePanelAction(
      normalizedState,
      action as { payload?: { panelId?: string } },
    )
  ) {
    return normalizedState;
  }
  const panelId = getPanelIdFromAction(
    action as { payload?: { panelId?: string } },
  );
  if (!panelId) {
    return normalizedState;
  }
  const currentPanelState = normalizedState.byId[panelId];
  const nextPanelState = slice.reducer(
    currentPanelState ?? initialSoundingsState,
    action,
  );

  if (nextPanelState === currentPanelState) {
    return normalizedState;
  }

  return {
    ...normalizedState,
    byId: {
      ...normalizedState.byId,
      [panelId]: nextPanelState,
    },
  };
};
